import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { useTable, useRowSelect, Column } from 'react-table';
import clsx from 'clsx';
import ReactPaginate from 'react-paginate';
import { FcPrevious, FcNext } from 'react-icons/fc';
import SelectMenu, { ISelectMenuItemValue } from '../../form-control/SelectMenu';
import Loader from '../../Loader';
import NotFoundComponent from '../../NotFoundComponent';
import IndeterminateCheckbox from '../IndeterminateCheckbox';
import { ITableFilterItem } from '../../../resources/interfaces';
import { EllipsisVerticalIcon, XMarkIcon } from '@heroicons/react/24/solid';
import Retry from '../../../views/errors/Retry';
import ActionsPopOnMe from '../../ActionsPopOnMe';
import GTableSearch from './InGTableComponents/GTableSearch';
import { renderFilterValue } from '../../../utils/filterUtils';
import { sentenceCase } from '../../../utils/miscFunctions';
import PopOnMe from '../poponme';
import Tooltip from '../../ToolTip';
import { useSearchParams } from 'react-router-dom';
import { FileUp } from 'lucide-react';
import ToolTip from '../../ToolTip';


const GTable: React.FC<ITableProps> = ({
  columns,
  data = [],
  selectionChanged,
  classes,
  stickyHeader = true,
  showSearch = true,
  searchClasses,
  searchPlaceholder = 'Search',
  onSearch,
  topActions = [],
  tableType,
  showPagination = true,
  paginationData,
  onPageChange,
  showPerPage,
  onShowPerPageChange,
  onRowClick,
  loading,
  columnClasses,
  propBasedColumnClasses,
  actionElement,
  showDateRangeSelector = false,
  onDateChange,
  otherTopActions,
  tableId,
  tableContainerId,
  showHeaders = true,
  showFilter,
  selectedFilters,
  onRemoveFilter,
  networkError,
  headerClasses,
  tableName,
  isRowLinked = true,
  setSelectedRows,
  exportFilteredData,
  fixedHeaderScrollHeight,
  highlightSelectedRow = false,
  highlightRowWhen,
}) => {
  const {
    getTableProps,
    headerGroups,
    rows,
    prepareRow,
    // @ts-ignore - toggleAllRowsSelected comes from the useRowSelect plugin,
    // not merged into the base instance type by the installed @types/react-table.
    toggleAllRowsSelected,
    //@ts-ignore
    state: { selectedRowIds, sorting }
  } = useTable(
    {
      columns,
      data,
      // Keep row selection alive across re-renders. Several callers pass a `data`
      // array whose identity changes on every render (e.g. a useMemo that depends
      // on an object rebuilt each render). react-table's autoResetSelectedRows
      // (default true) then clears the selection whenever `data`'s identity
      // changes, so a click would toggle a checkbox on and it would immediately
      // revert on the next render. Disable the auto-reset so selection persists.
      // @ts-ignore - autoResetSelectedRows comes from the useRowSelect plugin,
      // which the installed @types/react-table does not merge into TableOptions.
      autoResetSelectedRows: false,
      stateReducer: (newState, action) => {
        if (action.type === 'toggleRowSelected') {
          // const selected = newState.selectedRowIds;
          // const selectedData = data.filter((row: any, index: number) => selected[index]);
          setSelectedRows?.(newState);
        }
        return newState;
      }
    },
    useRowSelect,
    hooks => {
      // ... hooks configuration
    }
  );

  const [searchValue, setSearchValue] = useState('');
  const refTable = useRef<any>(null)
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [screenWidth, setScreenWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    if (typeof window !== 'undefined') {
      handleResize();
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  const scrollHeight = fixedHeaderScrollHeight ||
    `${screenWidth < 475 ? '35vh' : screenWidth < 768 ? '40vh' : screenWidth < 1024 ? '50vh' : screenWidth < 1280 ? '60vh' : '65vh'}`;

  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = Number(searchParams.get('page')) ?? 0;
  const allSelectedFilters = selectedFilters && selectedFilters?.filter((filter: ITableFilterItem, index: number) => (filter.value !== '' && filter.value2 !== ''))
  const isFilterSelected = (exportFilteredData && allSelectedFilters && allSelectedFilters.length > 0);

  useEffect(() => {
    const selectedTableIndexes: number[] = [];
    const selectedObjects: any[] = [];
    Object.keys(selectedRowIds)?.forEach((key: string) => {
      const idx = Number(key);
      const row = data[idx];
      // With autoResetSelectedRows disabled a selected index can briefly outlive
      // the row it pointed at (e.g. right after paginating to a shorter page).
      // Skip those so callers never receive `undefined` rows and crash on
      // `row.ref` / `row.id`.
      if (row === undefined) return;
      selectedTableIndexes.push(idx);
      selectedObjects.push(row);
    });
    selectionChanged?.(selectedObjects, selectedTableIndexes);
  }, [selectedRowIds]);

  // Selection is keyed by row index and persists across re-renders (auto-reset
  // is off). When the page changes the same indexes would otherwise appear
  // selected on the new page's rows, so clear the selection on page change.
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) { didMountRef.current = true; return; }
    toggleAllRowsSelected?.(false);
  }, [currentPage]);

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onSearch?.(searchValue);
    }

  };

  const handleSubmitSearch = () => {
    if (searchValue) onSearch?.(searchValue);
  };

  const handleRetry = () => {
    window.location.reload()
  }


  return (
    <>
      {loading ? (
        <div className='py-8'>
          <Loader message={`Loading ${tableType ?? 'data'}. Please wait...`} brollyLoaderFixed />
        </div>

      ) : (
        !networkError ?
          <div>
            {(showFilter || showSearch || otherTopActions) && <div className="flex flex-col flex-wrap md:flex-row justify-between gap-5 my-5">
              <div className="flex flex-col md:flex-row gap-5">
                {showFilter && <>{showFilter}</>}

                {showSearch && <GTableSearch searchPlaceholder={searchPlaceholder} searchClasses={searchClasses}
                  handleSearchInputChange={handleSearchInputChange}
                  handleKeyPress={handleKeyPress}
                  handleSubmitSearch={handleSubmitSearch} />
                }
              </div>

              <div className="flex flex-col md:flex-row gap-5 md:flex-end items-center">
                {otherTopActions && (
                  <div className="flex items-center gap-5">
                    {Array.isArray(otherTopActions) ? (
                      <ul className="flex gap-4 items-center">
                        {otherTopActions?.map((action: ITableTopActionItem, index: number) => (
                          <li
                            key={index}
                            className={clsx(
                              'hover:underline underline-offset-8 decoration-2 decoration-primary-main cursor-pointer',
                              { underline: action.active }
                            )}
                            onClick={action.callback}>
                            {action.label}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      otherTopActions
                    )}
                  </div>
                )}
                {showPagination && paginationData && (
                  <div className="dark:text-darkBg-subText flex flex-row items-center space-x-4 mr-4 w-full shrink-0">
                    <span className="block text-sm">
                      {paginationData.from ? paginationData.from : 0} to {paginationData.to ? paginationData.to : 0} of {paginationData.total ? paginationData.total : 0} items
                    </span>

                    <div className="space-x-2 flex items-center">
                      <span className="text-sm">Show</span>
                      <SelectMenu
                        name=""
                        listSelectedValue={String(showPerPage)}
                        onChange={(val: ISelectMenuItemValue) => {
                          onShowPerPageChange?.(Number(val ?? 25));
                        }}
                        list={[25, 50, 75, 100]}
                      />
                    </div>
                    {actionElement && <>{actionElement}</>}
                  </div>
                )}
              </div>
            </div>}
            {(selectedFilters || isFilterSelected) && <div className="flex items-center justify-between flex-nowrap space-y-1 pb-2 gap-8">
              {selectedFilters && (

                <div className='flex items-center justify-start flex-wrap  space-y-1'>
                  {selectedFilters.map((filter: ITableFilterItem, index: number) => {
                    // Skip empties early so the .map() child is always the
                    // keyed <div> — wrapping the conditional in <>…</> leaves
                    // the map child unkeyed and triggers the React warning.
                    const hasValue = filter?.value || filter.value2 || filter?.value?.to || filter?.value?.from;
                    if (!hasValue) return null;
                    return (
                      <div
                        key={index}
                        className="whitespace-nowrap bg-teal-600 text-white px-2 py-1 mr-2 rounded flex text-xs items-center gap-1"
                      >
                        <span>{filter.label} :</span>
                        {sentenceCase(String(renderFilterValue(filter)))}
                        <XMarkIcon
                          className="w-[16px] ml-2 cursor-pointer"
                          onClick={() => onRemoveFilter?.(filter, index)}
                        />
                      </div>
                    );
                  })}
                </div>
              )}

              {isFilterSelected && <ToolTip message='Export filtered policies' placement='bottom'>
                <div onClick={exportFilteredData} className='flex text-xs items-center py-[3px] px-[6px] justify-center relative right-[45px] border border-primary-main rounded-full text-primary-main'>
                  <FileUp className={`h-4 w-4  mr-2 `} /> Export
                </div>
              </ToolTip>}
            </div>}

            <div
              id={tableContainerId}
              ref={refTable}
              className={clsx(` overflow-x-auto ${stickyHeader ? 'overflow-y-auto' : 'overflow-y-hidden'} text-sm`, classes)}
              style={{
                maxHeight: stickyHeader ? scrollHeight : undefined
              }}>
              <table {...getTableProps()} className="w-full px-[0.5rem] relative" id={tableId}>
                {showHeaders && (
                  <thead className={`dark:border dark:border-darkBg-border z-[10] ${stickyHeader && 'sticky -top-[2px]'} `}>
                    {headerGroups.map((headerGroup: any) => {
                      // react-table's getHeaderGroupProps() / getHeaderProps()
                      // / getRowProps() / getCellProps() each return an object
                      // that already includes a `key`. Spreading them after a
                      // manual `key={…}` triggers React 18's "spread props
                      // containing key" warning, so we destructure the key out
                      // and pass it to JSX directly.
                      const { key: hgKey, ...hgProps } = headerGroup.getHeaderGroupProps();
                      return (
                        <tr
                          key={hgKey}
                          {...hgProps}
                          className={clsx(!headerClasses && "first:hidden px-6 py-3 text-left bg-gray-50 dark:bg-darkBg rounded", headerClasses)}>
                          {headerGroup.headers.map((column: any) => {
                            const isLastColumn = (String(column.Header).toLowerCase() === "action" || String(column.id).toLowerCase() === "action");
                            const sticky = column.meta?.sticky;
                            const offset = column.meta?.offset;
                            if (column.restricted) return null;
                            const { key: thKey, ...thProps } = column.getHeaderProps();
                            return (
                              <th
                                key={thKey}
                                {...thProps}
                                className={clsx(
                                  'px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap',
                                  isLastColumn && 'sticky -right-[1px] bg-gray-50 dark:bg-darkBg',
                                  sticky === 'left' && 'sticky bg-gray-50 dark:bg-darkBg',
                                )}
                                style={{
                                  left: sticky === 'left' ? offset : undefined
                                }}>
                                {column.render('Header')}
                              </th>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </thead>
                )}

                <tbody className="bg-white dark:bg-darkBg  divide-y divide-gray-200 dark:divide-darkBg-border border-[1px] border-gray-100 dark:border-darkBg-border ">
                  {rows.map((row: any) => {
                    prepareRow(row);
                    const { key: rowKey, ...rowProps } = row.getRowProps();
                    return (
                      <tr
                        key={rowKey}
                        {...rowProps}
                        className={clsx('border-b border-b-gray-200 dark:border-b-darkBg-border dark:border-darkBg-border hover:bg-gray-50 dark:hover:bg-transparent', {
                          'cursor-pointer': onRowClick,
                          // Selected-row highlight — off by default, opted in
                          // by callers that use selection as "focus this
                          // record" (e.g. the fleet Vehicles table where the
                          // operator picks a car and expects it to stay lit).
                          // Two opt-in paths: react-table selection state
                          // (highlightSelectedRow + row.isSelected) OR an
                          // external predicate (highlightRowWhen) for tables
                          // that track "focused row" in their own state.
                          //
                          // Uses Tailwind's `!` important marker so the amber
                          // wins over the base `hover:bg-gray-50` on the row
                          // (utility order in Tailwind's stylesheet is not
                          // guaranteed to favour the amber otherwise, which
                          // caused the highlight to only render on hover
                          // rather than persist on the selected row).
                          '!bg-amber-50 dark:!bg-amber-950/40 hover:!bg-amber-100 dark:hover:!bg-amber-950/60':
                            (highlightSelectedRow && row.isSelected)
                            || (typeof highlightRowWhen === 'function' && highlightRowWhen(row)),
                        }, isRowLinked && rows.length > 0 && ' hover:shadow-md')}>
                        {row.cells.map((cell: any) => {
                          const isLastRow = (String(cell.column.Header).toLowerCase() === "action" || String(cell.column.id).toLowerCase() === "action");
                          const sticky = cell.column.meta?.sticky;
                          const offset = cell.column.meta?.offset;
                          if (cell.column.restricted) return null;
                          const { key: cellKey, ...cellProps } = cell.getCellProps();
                          return (
                            <td
                              key={cellKey}
                              {...cellProps}
                              className={clsx(
                                'text-left px-6 py-4 dark:text-darkBg-mainText text-sm font-medium text-gray-700 whitespace-nowrap text-ellipsis',
                                columnClasses,
                                propBasedColumnClasses?.(row.original),
                                isLastRow && 'sticky -right-[1px] bg-white dark:bg-darkBg',
                                sticky === 'left' && 'sticky  bg-white dark:bg-darkBg',
                              )}
                              style={{
                                left: sticky === 'left' ? offset : undefined
                              }}
                              onClick={() => {
                                if (cell?.column?.id !== 'selection') {
                                  onRowClick?.(row.original);
                                }
                              }}>
                              {cell.render('Cell')}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {!loading && data && data.length === 0 && (
              <div className="w-full h-full text-center py-10">
                <NotFoundComponent text={`No ${tableType} found`} />
              </div>
            )}

            {showPagination && paginationData && paginationData.totalPages > 1 && (
              <div className={clsx(
                'sticky bottom-0 bg-white dark:bg-darkBg border-t border-gray-200 dark:border-darkBg-border z-10',
                'flex justify-center items-center p-4 space-x-2'
              )}>
              <ReactPaginate
                forcePage={currentPage}
                breakLabel="..."
                breakLinkClassName="dark:text-white"
                nextLabel={<FcNext />}
                previousLabel={<FcPrevious />}
                onPageChange={(event) => {
                  onPageChange?.(event.selected);
                }}
                pageRangeDisplayed={3}
                marginPagesDisplayed={1}
                pageCount={
                  paginationData.totalPages
                    ? paginationData.totalPages
                    : Math.ceil(paginationData.total / paginationData.per_page)
                }
                containerClassName="flex justify-center items-center space-x-2"
                pageClassName="w-[46px] h-[36px] text-[#8A8383] border border-[#D8E5EB] dark:border-darkBg-border rounded flex justify-center items-center text-base cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                pageLinkClassName="w-full h-full flex justify-center items-center"
                activeClassName="bg-primary-main"
                activeLinkClassName="text-dark"
              />
              </div>
            )}
          </div> :
          <>
            {!loading && networkError && (
              <div className="w-full h-full text-center py-10">
                <Retry onRetry={handleRetry} />
              </div>
            )}
          </>
      )}
    </>
  );
};



export const OtherActionsElement = ({ getOtherActions }: any) => {
  return (
    <ActionsPopOnMe actions={getOtherActions()}>
      <EllipsisVerticalIcon className="h-6 w-6 " />
    </ActionsPopOnMe>
  )
}

export const createSelectionColumn = <T extends object = any>(props: SelectionColumnProps = {}): Column<T> => {
  const {
    id = 'selection',
    className = '',
    // Default to IndeterminateCheckbox (not a raw 'input'): react-table's
    // toggle props include an `indeterminate` boolean, and spreading that onto a
    // native input triggers React's "Received `false` for a non-boolean
    // attribute `indeterminate`" warning. The wrapper applies it via a ref.
    checkboxComponent: Checkbox = IndeterminateCheckbox,
    checkboxProps = {},
    showSelectAll = true,
  } = props;

  return {
    id,
    width: 40,
    Header: ({ getToggleAllRowsSelectedProps }: any) => {
      if (!showSelectAll) return null;
      const { title, ...headerCheckboxProps } = getToggleAllRowsSelectedProps();
      return (
        <div className={className} title={title as string}>
          <Checkbox
            type="checkbox"
            {...checkboxProps}
            {...headerCheckboxProps}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              headerCheckboxProps.onChange?.(e);
              checkboxProps.onChange?.(e);
            }}
          />
        </div>
      );
    },
    Cell: ({ row }: any) => {
      const { title, ...rowCheckboxProps } = row.getToggleRowSelectedProps();
      return (
        <div className={className} title={title as string}>
          <Checkbox
            type="checkbox"
            {...checkboxProps}
            {...rowCheckboxProps}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              rowCheckboxProps.onChange?.(e);
              checkboxProps.onChange?.(e);
            }}
          />
        </div>
      );
    },
  };
};

export const createSelectSingleColumn = <T extends object = any>(props: SelectionColumnProps = {}): Column<T> => {
  const { id = 'selection' } = props;

  return {
    id,
    width: 40,
    Header: () => null,
    Cell: ({ row, rows }: any) => {
      return (
        <div>
          <input
            type="radio"
            name={`table-radio-${row.id}`}
            className="h-5 w-5 appearance-none rounded-full border-2 border-gray-400 
                     checked:bg-primary-main checked:border-primary-main"
            checked={row.isSelected}
            onChange={() => {
              // Clear all other rows first
              rows.forEach((r: any) => {
                if (r.id !== row.id) {
                  r.toggleRowSelected(false);
                }
              });
              // Then toggle the current row
              row.toggleRowSelected(!row.isSelected);
            }}
          />
        </div>
      );
    },
  };
};

export const GTableActionColumn = ({ actions }: ITableRowActionIconProps): JSX.Element => {
  return (
    
      <div className="cursor-pointer flex justify-center">
        {actions.length > 0 && (
          <PopOnMe actions={actions}>
            <EllipsisVerticalIcon className="h-5 w-5" />
          </PopOnMe>
        )}
      </div>
   
  )
}

export default GTable;

export interface SelectionColumnProps {
  id?: string;
  className?: string;
  checkboxComponent?: React.ComponentType<{
    checked: boolean;
    onChange: (checked: boolean) => void;
    [key: string]: any;
  }>;
  checkboxProps?: Record<string, any>;
  showSelectAll?: boolean;
}

interface ITableProps {
  columns: any;
  data: any[];
  selectionChanged?: (selectedObjects: any, selectedIndexes: number[]) => void;
  classes?: string;
  stickyHeader?: boolean;
  showSearch?: boolean;
  searchClasses?: string;
  searchPlaceholder?: string;
  onSearch?: (searchString: string) => void;
  topActions?: ITableTopActionItem[] | JSX.Element | React.ReactNode;
  otherTopActions?: JSX.Element | React.ReactNode;
  tableType?: string;
  showPagination?: boolean;
  paginationData?: ITablePaginationData;
  onPageChange?: (page: number) => void;
  showPerPage?: number;
  onShowPerPageChange?: (page: number) => void;
  onRowClick?: (data: any) => void;
  loading?: boolean;
  columnClasses?: string;
  propBasedColumnClasses?: (data: any) => string;
  actionElement?: JSX.Element | React.ReactNode;
  showDateRangeSelector?: boolean;
  onDateChange?: (data: any) => void;
  tableId?: string;
  tableContainerId?: string;
  showHeaders?: boolean;
  showCheckboxes?: boolean;
  showFilter?: JSX.Element | Element;
  selectedFilters?: ITableFilterItem[];
  onRemoveFilter?: (filter: ITableFilterItem, index: number) => void;
  networkError?: boolean,
  headerClasses?: string,
  tableName?: string,
  isRowLinked?: boolean,
  setSelectedRows?: (data: any) => void;
  exportFilteredData?: (data?: any) => void;
  fixedHeaderScrollHeight?: string;
  /**
   * When true, apply a persistent background highlight to rows whose
   * react-table selection state is on (i.e. the checkbox is ticked).
   * Off by default so tables that use selection only for bulk actions
   * (bulk pay, bulk export, etc.) don't get unexpected row styling.
   * Turned on by callers who use selection as "focus this record"
   * (fleet Vehicles list — the operator picks a vehicle and expects
   * it to stay visibly selected while they act on it).
   */
  highlightSelectedRow?: boolean;
  /**
   * Callback-driven highlight for tables that track "selected record" via
   * external state (not react-table selection). Return true for the row
   * that should be highlighted. Complement to highlightSelectedRow — use
   * this when the caller drives selection through its own state instead
   * of checkboxes (single-policy detail's Vehicles table: it stores the
   * chosen registration number in local state to drive the details
   * section below, but the table itself has no checkbox column).
   */
  highlightRowWhen?: (row: any) => boolean;
}
export interface ITableTopActionItem {
  id: string;
  label: string;
  callback: () => void | Promise<void>;
  active: boolean;
}
export interface ITableRowAction {
  label: string;
  icon?: JSX.Element | ReactElement;
  callback: (data?: any) => void | Promise<void>;
  data?: any;
}
export interface ITableAction {
  label: string;
  callback: (data?: any) => void | Promise<void>;
  icon?: React.ReactNode;
}
export interface ITablePaginationData {
  total: number;
  per_page: number;
  totalPages: number;
  from?: number;
  to?: number;
  initialPage?: number;
}

interface ITableRowActionIconProps {
  actions: Array<ITableRowAction>;
}